'use client';

import { reportError } from '@/utils/error-reporting';
import { useDocumentInfo } from '@payloadcms/ui';
import { useEffect, useState } from 'react';

import type { User } from '@/payload-types';

import './styles.scss';

interface AuthorState {
  createdBy: User | null;
  updatedBy: User | null;
  isLoading: boolean;
}

type RelationshipValue = string | { id: string } | { value: RelationshipValue };

interface DocumentDataWithAuthors {
  createdBy?: RelationshipValue;
  updatedBy?: RelationshipValue;
  [key: string]: unknown;
}

const fetchUser = async (userId: string): Promise<User | null> => {
  try {
    const response = await fetch(`/api/users/${userId}`, { credentials: 'include' });
    return response.ok ? await response.json() : null;
  } catch (error) {
    reportError(error, { userId });
    return null;
  }
};

// Helper to get user display name (currently users only have email)
const getUserDisplayName = (user: User): string => user.email;

const extractUserId = (value: RelationshipValue | null | undefined): string | undefined => {
  if (!value) return;
  if (typeof value === 'string') return value;
  if (typeof value === 'object' && 'id' in value) return value.id;
  if (typeof value === 'object' && 'value' in value) {
    return extractUserId(value.value);
  }
  return;
};

export const AuthorInfoDescription = () => {
  const docInfo = useDocumentInfo();
  const documentData = docInfo?.data as DocumentDataWithAuthors | undefined;

  const [state, setState] = useState<AuthorState>({
    createdBy: null,
    updatedBy: null,
    isLoading: true,
  });

  useEffect(() => {
    let isMounted = true;

    const loadUsers = async () => {
      const createdByValue = documentData?.createdBy;
      const updatedByValue = documentData?.updatedBy;

      const createdById = extractUserId(createdByValue);
      const updatedById = extractUserId(updatedByValue);

      if (!createdById && !updatedById) {
        if (isMounted) setState({ createdBy: null, updatedBy: null, isLoading: false });
        return;
      }

      if (createdById === updatedById && createdById) {
        const user = await fetchUser(createdById);
        if (isMounted) {
          setState({ createdBy: user, updatedBy: user, isLoading: false });
        }
        return;
      }

      const [createdByUser, updatedByUser] = await Promise.all([
        createdById ? fetchUser(createdById) : null,
        updatedById ? fetchUser(updatedById) : null,
      ]);

      if (isMounted) {
        setState({ createdBy: createdByUser, updatedBy: updatedByUser, isLoading: false });
      }
    };

    loadUsers();

    return () => {
      isMounted = false;
    };
  }, [documentData]);

  if (state.isLoading) return null;
  if (!state.createdBy && !state.updatedBy) return null;

  return (
    <div className="author-info-description">
      {state.createdBy && (
        <span className="author-info-inline">
          Created by: <strong>{getUserDisplayName(state.createdBy)}</strong>
        </span>
      )}
      {state.updatedBy && (
        <span className="author-info-inline">
          Updated by: <strong>{getUserDisplayName(state.updatedBy)}</strong>
        </span>
      )}
    </div>
  );
};
